import {
  getConnectedPatient,
  getPatientRegisters,
  getPatientsMedications,
  PatientRegistersResponse,
} from "@/app/api/patient";

import {
  PatientDTO,
  MedicationDTO,
  PatientMedicationsResponse,
} from "@/app/api/patient/types";

import { ErrorResponse } from "@/app/types/error";
import { IDailyEntry } from "@/app/types/registers";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

interface PatientState {
  data: { patient: PatientDTO; registers: IDailyEntry[] };
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  medications: MedicationDTO[];
  medicationsStatus: "idle" | "loading" | "succeeded" | "failed";
  medicationsError: string | null;
}

interface GetRegistersArgs {
  id: string;
  week?: number;
}

const initialState: PatientState = {
  data: { patient: {} as PatientDTO, registers: [] },
  status: "idle",
  error: null,
  medications: [],
  medicationsStatus: "idle",
  medicationsError: null,
};

export const getPatientById = createAsyncThunk(
  "provider/patient",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await getConnectedPatient(id);

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === "fail"
      ) {
        return rejectWithValue((response as ErrorResponse).message);
      }

      return response as PatientRegistersResponse;
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  },
);

export const getPatientRegistersByWeek = createAsyncThunk<
  PatientRegistersResponse,
  GetRegistersArgs,
  { rejectValue: string }
>("providers/patient/registers", async ({ id, week }, { rejectWithValue }) => {
  try {
    const response = await getPatientRegisters(id, week);

    if (
      response &&
      typeof response === "object" &&
      "status" in response &&
      response.status === "fail"
    ) {
      return rejectWithValue((response as ErrorResponse).message);
    }

    return response as PatientRegistersResponse;
  } catch (error) {
    if (error instanceof Error && error.message) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue("An unknown error occurred");
  }
});

export const getPatientMedicationsById = createAsyncThunk<
  PatientMedicationsResponse,
  string,
  { rejectValue: string }
>("provider/patient/medications", async (patientId, { rejectWithValue }) => {
  try {
    const response = await getPatientsMedications(patientId);

    if (
      response &&
      typeof response === "object" &&
      "status" in response &&
      response.status === "fail"
    ) {
      return rejectWithValue((response as ErrorResponse).message);
    }

    return response as PatientMedicationsResponse;
  } catch (error) {
    if (error instanceof Error && error.message) {
      return rejectWithValue(error.message);
    }

    return rejectWithValue("An unknown error occurred");
  }
});

//TODO revisar resetStatus

const patientSlice = createSlice({
  name: "patient",
  initialState,
  reducers: {
    resetStatus: (state) => {
      state.status = "loading";
    },
    resetPatientData: () => ({
      ...initialState,
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPatientById.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getPatientById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        state.data = action.payload.data;
      })
      .addCase(getPatientById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      })
      .addCase(getPatientRegistersByWeek.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getPatientRegistersByWeek.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        state.data = action.payload.data;
      })
      .addCase(getPatientRegistersByWeek.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      })
      .addCase(getPatientMedicationsById.pending, (state) => {
        state.medicationsStatus = "loading";
        state.medicationsError = null;
      })
      .addCase(getPatientMedicationsById.fulfilled, (state, action) => {
        state.medicationsStatus = "succeeded";
        state.medicationsError = null;
        state.medications = action.payload.data;
      })
      .addCase(getPatientMedicationsById.rejected, (state, action) => {
        state.medicationsStatus = "failed";
        state.medicationsError = action.payload || action.error.message || null;
      });
  },
});

export const { resetStatus, resetPatientData } = patientSlice.actions;

export const patientReducer = patientSlice.reducer;
